import { type ResolveAsset, SceneRenderer } from "@cva/renderer";
import {
  type ClipFraming,
  clipAt,
  deviceClipScreen,
  elementsOnHiddenTracks,
  layoutClips,
  type OverlayBox,
  type OverlayClip,
  overlayClipsAt,
  placeClip,
  placeOverlay,
  quadBounds,
  type SceneDocument,
  type VideoClip,
  type VideoStack,
  videoSafeZones,
  videoStack,
} from "@cva/shared";
import { useCallback, useMemo, useRef, useState } from "react";
import { useEnv } from "../env";
import { useEditor, useEditorApi } from "../store";
import { useCaptions } from "./data";
import { useVideo } from "./store";
import { VideoLayer } from "./VideoLayer";

/**
 * The page of a video document at the playhead, with the video picture below
 * and the clips of higher tracks between the bands of elements (video/tracks.ts).
 */
export function VideoPageRenderer({
  doc,
  hiddenIds,
  resolveAsset,
}: {
  doc: SceneDocument;
  hiddenIds: ReadonlySet<string>;
  resolveAsset: ResolveAsset;
}) {
  const time = useVideo((s) => s.time);
  const playing = useVideo((s) => s.playing);
  const { pages, captionsElement } = useCaptions();
  // The same object while the stack stays the same (the player re-reads its data on every change).
  const stackJson = JSON.stringify(videoStack(doc));
  const stack = useMemo(() => JSON.parse(stackJson) as VideoStack, [stackJson]);
  const bands = stack.bands.length;
  // One host per band: the canvas of the video slot above it goes there.
  const [hosts, setHosts] = useState<(HTMLDivElement | null)[]>([]);
  const setHost = useCallback((index: number, element: HTMLDivElement | null) => {
    setHosts((current) => {
      if (current[index] === element) return current;
      const next = [...current];
      next[index] = element;
      return next;
    });
  }, []);
  const interlays = useMemo(
    () =>
      Array.from({ length: bands }, (_, index) => (
        <div
          // biome-ignore lint/suspicious/noArrayIndexKey: one host per band
          key={index}
          ref={(element) => setHost(index, element)}
          style={{ position: "absolute", inset: 0 }}
        />
      )),
    [bands, setHost],
  );
  const shownHosts = useMemo(() => hosts.slice(0, bands), [hosts, bands]);
  // Elements on hidden tracks are left out like in the export.
  const hidden = useMemo(() => {
    const onHidden = elementsOnHiddenTracks(doc);
    return onHidden.size === 0 ? hiddenIds : new Set([...hiddenIds, ...onHidden]);
  }, [doc, hiddenIds]);
  return (
    <SceneRenderer
      document={doc}
      time={time}
      animate={playing}
      captions={pages}
      captionPlaceholder={
        captionsElement && pages.length === 0 ? "Hier erscheinen deine Untertitel" : undefined
      }
      hiddenIds={hidden}
      resolveAsset={resolveAsset}
      underlay={<VideoLayer stack={stack} hosts={shownHosts} />}
      interlays={interlays}
    />
  );
}

/**
 * Areas the platforms cover or cut off for this format (`videoSafeZones`):
 * the Reels/TikTok/Shorts interface in 9:16, the 3:4 profile grid in 4:5
 * and 1:1. Landscape videos have none.
 */
export function SafeZones({
  width,
  height,
  zoom,
}: {
  width: number;
  height: number;
  zoom: number;
}) {
  const show = useVideo((s) => s.safeZones);
  const safe = show ? videoSafeZones({ width, height }) : null;
  if (!safe) return null;
  const zone = "absolute bg-rose-500/20 outline-dashed outline-rose-500/70";
  const outline = { outlineWidth: 1.5 / zoom };
  const unit = Math.min(width, height) / 1080;
  return (
    <div className="pointer-events-none absolute" style={{ left: 0, top: 0, width, height }}>
      {safe.zones.map((rect, index) => (
        <div
          // biome-ignore lint/suspicious/noArrayIndexKey: zones have no identity
          key={index}
          className={zone}
          style={{ ...outline, left: rect.x, top: rect.y, width: rect.width, height: rect.height }}
        />
      ))}
      <span
        className="absolute rounded bg-rose-600 px-1.5 py-0.5 whitespace-nowrap text-white"
        style={{ left: 16 * unit, top: 16 * unit, fontSize: 12 / zoom }}
      >
        {safe.label}
      </span>
    </div>
  );
}

/** The main track clip at `time` when it has a box (split screen) that contains `point`. */
export function boxedMainClipAtPoint(
  doc: SceneDocument,
  time: number,
  point: { x: number; y: number },
): VideoClip | null {
  const clip = clipAt(layoutClips(doc.timeline), time)?.clip;
  const box = clip?.box;
  if (!clip || !box) return null;
  return point.x >= box.x &&
    point.x <= box.x + box.width &&
    point.y >= box.y &&
    point.y <= box.y + box.height
    ? clip
    : null;
}

/** The topmost clip of an upper video track visible at `time` whose box contains `point`. */
export function overlayClipAtPoint(
  doc: SceneDocument,
  time: number,
  point: { x: number; y: number },
): OverlayClip | null {
  const visible = overlayClipsAt(doc.timeline, time);
  for (let index = visible.length - 1; index >= 0; index--) {
    const { box } = visible[index]!;
    if (
      point.x >= box.x &&
      point.x <= box.x + box.width &&
      point.y >= box.y &&
      point.y <= box.y + box.height
    ) {
      return visible[index]!;
    }
  }
  return null;
}

/** Moves a box onto the frame edges and centre when it comes close. */
export function snapBoxToFrame(
  box: OverlayBox,
  frame: { width: number; height: number },
  threshold: number,
): { x: number; y: number } {
  const snap = (value: number, size: number, total: number) => {
    for (const target of [0, total - size, (total - size) / 2]) {
      if (Math.abs(value - target) <= threshold) return target;
    }
    return value;
  };
  return {
    x: snap(box.x, box.width, frame.width),
    y: snap(box.y, box.height, frame.height),
  };
}

type BoxHandle = "nw" | "n" | "ne" | "e" | "se" | "s" | "sw" | "w";

const HANDLES: { id: BoxHandle; x: number; y: number; cursor: string }[] = [
  { id: "nw", x: 0, y: 0, cursor: "nwse-resize" },
  { id: "n", x: 0.5, y: 0, cursor: "ns-resize" },
  { id: "ne", x: 1, y: 0, cursor: "nesw-resize" },
  { id: "e", x: 1, y: 0.5, cursor: "ew-resize" },
  { id: "se", x: 1, y: 1, cursor: "nwse-resize" },
  { id: "s", x: 0.5, y: 1, cursor: "ns-resize" },
  { id: "sw", x: 0, y: 1, cursor: "nesw-resize" },
  { id: "w", x: 0, y: 0.5, cursor: "ew-resize" },
];

/**
 * Frame of the selected clip of an upper video track on the canvas: handles
 * resize it (corners keep the proportions, with Shift freely). Moving happens
 * in the canvas itself, so elements above the video stay clickable.
 */
export function TrackClipBox({ zoom }: { zoom: number }) {
  const store = useEditorApi();
  const selected = useVideo((s) => s.selectedClip);
  const framing = useVideo((s) => s.framing);
  const time = useVideo((s) => s.time);
  const doc = useEditor((s) => s.doc);
  const readOnly = useEditor((s) => s.readOnly);
  const drag = useRef<{ handle: BoxHandle; x: number; y: number; box: OverlayBox } | null>(null);
  if (framing || (selected?.track !== "overlay" && selected?.track !== "video")) return null;
  const main = selected.track === "video";
  // Main track clips have a frame only in split screens and cards (their box).
  const entry = main
    ? layoutClips(doc.timeline).find((item) => item.clip.id === selected.id)
    : null;
  const clip = main ? entry?.clip : doc.timeline?.overlays?.find((item) => item.id === selected.id);
  const box = clip?.box;
  if (!clip || !box) return null;
  const visible = entry
    ? time >= entry.start && time < entry.end
    : time >= (clip as OverlayClip).start &&
      overlayClipsAt(doc.timeline, time).includes(clip as OverlayClip);
  const size = 10 / zoom;
  return (
    <div
      className="pointer-events-none absolute"
      style={{ left: box.x, top: box.y, width: box.width, height: box.height }}
    >
      <div
        className={
          visible ? "absolute inset-0 border-brand-500" : "absolute inset-0 border-brand-300"
        }
        style={{ borderWidth: 1.5 / zoom, borderStyle: visible ? "solid" : "dashed" }}
      />
      {readOnly
        ? null
        : HANDLES.map((handle) => (
            <span
              key={handle.id}
              className="pointer-events-auto absolute rounded-[2px] border-brand-500 bg-white"
              style={{
                left: handle.x * box.width - size / 2,
                top: handle.y * box.height - size / 2,
                width: size,
                height: size,
                borderWidth: 1.5 / zoom,
                cursor: handle.cursor,
              }}
              onPointerDown={(event) => {
                event.stopPropagation();
                (event.currentTarget as HTMLElement).setPointerCapture(event.pointerId);
                drag.current = { handle: handle.id, x: event.clientX, y: event.clientY, box };
                store.getState().beginGesture();
              }}
              onPointerMove={(event) => {
                const start = drag.current;
                if (!start) return;
                const next = resizeBox(
                  start.box,
                  start.handle,
                  (event.clientX - start.x) / zoom,
                  (event.clientY - start.y) / zoom,
                  !event.shiftKey,
                );
                store.getState().update((draft) => {
                  const target = main
                    ? draft.timeline?.clips.find((item) => item.id === clip.id)
                    : draft.timeline?.overlays?.find((item) => item.id === clip.id);
                  if (target) target.box = next;
                });
              }}
              onPointerUp={() => {
                drag.current = null;
                store.getState().endGesture();
              }}
            />
          ))}
    </div>
  );
}

/** The box after dragging a handle by (dx, dy); corners optionally keep the aspect ratio. */
function resizeBox(
  box: OverlayBox,
  handle: BoxHandle,
  dx: number,
  dy: number,
  keepAspect: boolean,
): OverlayBox {
  const MIN = 40;
  let { x, y, width, height } = box;
  if (handle.includes("w")) {
    width = Math.max(MIN, box.width - dx);
    x = box.x + box.width - width;
  }
  if (handle.includes("e")) width = Math.max(MIN, box.width + dx);
  if (handle.includes("n")) {
    height = Math.max(MIN, box.height - dy);
    y = box.y + box.height - height;
  }
  if (handle.includes("s")) height = Math.max(MIN, box.height + dy);
  if (keepAspect && handle.length === 2) {
    const aspect = box.width / box.height;
    // The larger change decides, the other side follows.
    if (Math.abs(width - box.width) / box.width > Math.abs(height - box.height) / box.height) {
      height = width / aspect;
    } else {
      width = height * aspect;
    }
    if (handle.includes("w")) x = box.x + box.width - width;
    if (handle.includes("n")) y = box.y + box.height - height;
  }
  return {
    x: Math.round(x),
    y: Math.round(y),
    width: Math.round(width),
    height: Math.round(height),
  };
}

/**
 * Framing mode: drag moves the picture of the selected clip inside the frame
 * (or inside the box of a clip on an upper track), the mouse wheel zooms it.
 */
export function FramingOverlay({ zoom }: { zoom: number }) {
  const store = useEditorApi();
  const env = useEnv();
  const framing = useVideo((s) => s.framing);
  const selected = useVideo((s) => s.selectedClip);
  const doc = useEditor((s) => s.doc);
  const drag = useRef<{ x: number; y: number; fx: number; fy: number } | null>(null);
  if (!framing || (selected?.track !== "video" && selected?.track !== "overlay")) return null;
  const clip =
    selected.track === "video"
      ? doc.timeline?.clips.find((entry) => entry.id === selected.id)
      : doc.timeline?.overlays?.find((entry) => entry.id === selected.id);
  if (!clip) return null;
  const asset = env.assets.get(clip.assetId);
  const source = { width: asset?.width ?? 1920, height: asset?.height ?? 1080 };
  // In a device the picture lies on its screen (a perspective screen: its bounding box).
  const screen = selected.track === "overlay" ? deviceClipScreen(clip as OverlayClip, asset) : null;
  const frame = screen
    ? quadBounds(screen)
    : (clip.box ?? { x: 0, y: 0, width: doc.format.width, height: doc.format.height });
  const placed =
    screen || clip.box
      ? placeOverlay(frame, source, clip.framing)
      : placeClip(frame, source, clip.framing);
  const update = (patch: Partial<ClipFraming>, merge?: string) =>
    store.getState().update(
      (draft) => {
        const target =
          selected.track === "video"
            ? draft.timeline?.clips.find((entry) => entry.id === clip.id)
            : draft.timeline?.overlays?.find((entry) => entry.id === clip.id);
        if (target) Object.assign(target.framing, patch);
      },
      merge ? { merge } : undefined,
    );
  return (
    <div
      data-framing=""
      className="absolute cursor-move"
      style={{ left: frame.x, top: frame.y, width: frame.width, height: frame.height }}
      onPointerDown={(event) => {
        event.stopPropagation();
        (event.currentTarget as HTMLElement).setPointerCapture(event.pointerId);
        drag.current = {
          x: event.clientX,
          y: event.clientY,
          fx: clip.framing.x,
          fy: clip.framing.y,
        };
        store.getState().beginGesture();
      }}
      onPointerMove={(event) => {
        const start = drag.current;
        if (!start) return;
        const dx = (event.clientX - start.x) / zoom;
        const dy = (event.clientY - start.y) / zoom;
        const rangeX = (frame.width - placed.width) / 2;
        const rangeY = (frame.height - placed.height) / 2;
        const clamp = (value: number) => Math.max(-1, Math.min(1, value));
        update({
          x: Math.abs(rangeX) > 1 ? clamp(start.fx + dx / rangeX) : clip.framing.x,
          y: Math.abs(rangeY) > 1 ? clamp(start.fy + dy / rangeY) : clip.framing.y,
        });
      }}
      onPointerUp={() => {
        drag.current = null;
        store.getState().endGesture();
      }}
      onWheel={(event) => {
        event.stopPropagation();
        const next = Math.max(
          0.2,
          Math.min(5, clip.framing.zoom * Math.exp(-event.deltaY * 0.002)),
        );
        update({ zoom: Math.round(next * 100) / 100 }, "framing-zoom");
      }}
    >
      <div
        className="pointer-events-none absolute border-brand-500"
        style={{
          left: placed.x - frame.x,
          top: placed.y - frame.y,
          width: placed.width,
          height: placed.height,
          borderWidth: 2 / zoom,
          borderStyle: "dashed",
        }}
      />
    </div>
  );
}

/** Clip under the playhead (for "Bildausschnitt" when nothing is selected). */
export function clipUnderPlayhead(doc: SceneDocument, time: number) {
  return clipAt(layoutClips(doc.timeline), time);
}
